import { AnalysisResult } from '@/gen/controls/v1/analysis_result_pb'

import type { EventProperties } from '@/lib/analytics/events'
import { formatTimestamp } from '@/lib/date/date'

import { RISK_QUESTIONNAIRE_THREAD_LABELS } from '@/pages/control/labels'
import { EarlierEvaluations } from '@/pages/control/risk-questionnaire-thread-card/earlier-evaluations'
import { EvaluationVerdict } from '@/pages/control/risk-questionnaire-thread-card/evaluation-verdict'
import { LemaAiIcon } from '@/pages/control/risk-questionnaire-thread-card/lema-ai-icon'
import { StatusChangeBadges } from '@/pages/control/risk-questionnaire-thread-card/status-change-badges'
import type { StatusChange } from '@/pages/control/risk-questionnaire-thread-card/types'
import { countStatusChanges } from '@/pages/control/risk-questionnaire-thread-card/utils'

import { TimelineItem } from '@/components/timeline/timeline-item'

type EvaluationGroupProps = {
  evaluations: AnalysisResult[]
  isHighlighted: boolean
  statusChanges: Map<string, StatusChange>
  readOnly: boolean
  trackingProps: EventProperties
}

export const EvaluationGroup = ({
  evaluations,
  isHighlighted,
  statusChanges,
  readOnly,
  trackingProps,
}: EvaluationGroupProps) => {
  const [newest, ...earlier] = evaluations
  const hasEarlier = earlier.length > 0
  const newestChange = statusChanges.get(newest.id)

  return (
    <TimelineItem
      fullWidth
      icon={<LemaAiIcon isHighlighted={isHighlighted} />}
      date={formatTimestamp(newest.createdAt, 'dateTime')}
      overrideUsername={RISK_QUESTIONNAIRE_THREAD_LABELS.LEMA_AI_VERDICT}
    >
      <div className='flex flex-col gap-2'>
        {hasEarlier && (
          <span className='text-main-foreground-tertiary text-body-small'>
            {RISK_QUESTIONNAIRE_THREAD_LABELS.EVALUATIONS_SUMMARY(
              evaluations.length,
              countStatusChanges(evaluations, statusChanges),
            )}
          </span>
        )}
        {newestChange && <StatusChangeBadges change={newestChange} />}
        <EvaluationVerdict
          key={newest.id}
          evaluation={newest}
          isHighlighted={isHighlighted}
          readOnly={readOnly}
          trackingProps={trackingProps}
        />
        {hasEarlier && (
          <EarlierEvaluations
            evaluations={earlier}
            statusChanges={statusChanges}
            trackingProps={trackingProps}
          />
        )}
      </div>
    </TimelineItem>
  )
}
